import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Award, Download, User, ClipboardList, ShieldCheck, X, CheckCircle, Calendar, Target, TrendingUp, Rocket } from 'lucide-react';
import { collection, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { exportEvaluationsToExcel } from '../lib/exports';
import { calculateIkrProgress, getDefaultEvaluationPeriod } from '../lib/utils';
import { getPersonIkrs, evalSubcollectionFor } from '../lib/personIkrs';
import ProgressDonut from '../components/ui/ProgressDonut';
import ProgressChart from '../components/ui/ProgressChart';
import { EmployeeSatisfactionModal, EvaluationRow } from '../features/evaluations';

/** How long to wait after the last keystroke before persisting a textarea edit. */
const CARD_WRITE_DEBOUNCE_MS = 500;

export default function EvaluationsView({ okrs, okrsFY2627, users, teams, viewer, db: dbProp, activeFY: activeFYProp }) {
    const dbInstance = dbProp || db;
    const [selectedPerson, setSelectedPerson] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState(() => getDefaultEvaluationPeriod(activeFYProp || 'FY2526'));
    const [evaluations, setEvaluations] = useState({});
    const [loading, setLoading] = useState(false);
    const [personSearchTerm, setPersonSearchTerm] = useState('');
    const [showPersonDropdown, setShowPersonDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [evaluationCards, setEvaluationCards] = useState({});
    const [meetingDate, setMeetingDate] = useState('');
    const [employeeStatus, setEmployeeStatus] = useState('pending');
    const [evaluationMode, setEvaluationMode] = useState('employee'); // 'employee' | 'manager' | 'admin'
    const [npsScore, setNpsScore] = useState(null);
    const [showSatisfactionModal, setShowSatisfactionModal] = useState(false);
    /** Export progress, so a long run reads as working rather than broken. */
    const [exportProgress, setExportProgress] = useState(null);
    const [exportError, setExportError] = useState('');
    /** Card edits not yet confirmed in Firestore, keyed by cardType. A snapshot echo can carry
     *  an older value than the user has already typed; letting it through rewrites the textarea
     *  and drops the caret at the end of the text. */
    const pendingCardEdits = useRef({});
    const cardWriteTimers = useRef({});
    /** Financial year comes from the header toggle — this tab has no switch of its own. */
    const evalFY = activeFYProp || 'FY2526';
    const deepLinkPeriodRef = useRef(false);

    const evalSubcollection = evalSubcollectionFor(evalFY);
    
    // Handle URL parameters for deep linking from email notifications
    useEffect(() => {
        const periodParam = sessionStorage.getItem('evaluationPeriod');
        const employeeParam = sessionStorage.getItem('evaluationEmployee');
        // 'evaluationFY' is consumed by App.js, which owns the FY toggle.

        if (periodParam) {
            setSelectedPeriod(periodParam);
            // Stop the FY effect below from overwriting a deep-linked period.
            deepLinkPeriodRef.current = true;
            sessionStorage.removeItem('evaluationPeriod');
        }
        
        if (employeeParam && Array.isArray(users) && users.length > 0) {
            // Find the employee in users array
            const employee = users.find(u => (u.uid || u.id) === employeeParam);
            if (employee) {
                setSelectedPerson(employeeParam);
                setPersonSearchTerm(employee.name || '');
                // Auto-switch to manager mode if viewer is the manager
                const viewerId = viewer.uid || viewer.id;
                const employeeDoc = users.find(u => (u.uid || u.id) === employeeParam);
                if (employeeDoc?.managerId === viewerId || employeeDoc?.managerEmail?.toLowerCase() === viewer.email?.toLowerCase()) {
                    setEvaluationMode('manager');
                }
            }
            sessionStorage.removeItem('evaluationEmployee');
        }
    }, [users, viewer]);
    
    // Keep the period on the quarter we're actually in for the selected FY.
    useEffect(() => {
        if (deepLinkPeriodRef.current) {
            deepLinkPeriodRef.current = false;
            return;
        }
        setSelectedPeriod(getDefaultEvaluationPeriod(evalFY));
    }, [evalFY]);

    // Check if user has direct reports (is a manager)
    const hasDirectReports = useMemo(() => {
        if (!viewer || !Array.isArray(users)) return false;
        const viewerId = viewer.uid || viewer.id;
        const viewerDoc = users.find(u => (u.uid || u.id) === viewerId);
        return viewerDoc?.directReports && Array.isArray(viewerDoc.directReports) && viewerDoc.directReports.length > 0;
    }, [viewer, users]);
    
    // Get available people for selection based on evaluation mode
    const availablePeople = useMemo(() => {
        if (!Array.isArray(users) || !viewer) return [];
        
        let people = [];
        
        if (evaluationMode === 'employee') {
            // Employee mode: Show only the current user
            people = [viewer];
        } else if (evaluationMode === 'manager') {
            // Manager mode: Show only direct reports
            const viewerId = viewer.uid || viewer.id;
            const viewerDoc = users.find(u => (u.uid || u.id) === viewerId);
            if (viewerDoc?.directReports && Array.isArray(viewerDoc.directReports)) {
                people = users.filter(user => {
                    const userId = user.uid || user.id;
                    return viewerDoc.directReports.includes(userId);
                });
            } else {
                // Fallback: match by managerEmail
                const viewerEmail = viewer.email?.toLowerCase().trim();
                people = users.filter(user => {
                    const userManagerEmail = user.managerEmail?.toLowerCase().trim();
                    return userManagerEmail === viewerEmail;
                });
            }
        } else if (evaluationMode === 'admin') {
            // Admin mode: Show all users
            people = users;
        } else {
            // Fallback to old logic for backward compatibility
        if (viewer.role === 'Admin') {
            people = users;
        } else if (viewer.role === 'L1 Team Lead' || viewer.role === 'L2 Team Lead') {
            people = users.filter(user => {
                if (viewer.role === 'L1 Team Lead') {
                    const userRoot = user.teamId?.trim().split('.')[0];
                    const viewerRoot = viewer.teamId?.trim().split('.')[0];
                    return userRoot === viewerRoot;
                } else {
                    return user.teamId?.trim() === viewer.teamId?.trim();
                }
            });
        } else {
            people = [viewer];
            }
        }
        
        // Filter by search term
        if (personSearchTerm.trim()) {
            const searchLower = personSearchTerm.toLowerCase();
            people = people.filter(person => 
                person.name?.toLowerCase().includes(searchLower) ||
                person.role?.toLowerCase().includes(searchLower) ||
                person.email?.toLowerCase().includes(searchLower)
            );
        }
        
        return people;
    }, [users, viewer, personSearchTerm, evaluationMode]);
    
    // Get IKRs for selected person (FY-aware). Shared with the Evaluations
    // Summary response viewer so both key evaluation documents identically.
    const personIKRs = useMemo(
        () => getPersonIkrs(selectedPerson, { okrs, okrsFY2627, fy: evalFY }),
        [okrs, okrsFY2627, selectedPerson, evalFY]
    );
    
    // Calculate summary metrics
    const summaryMetrics = useMemo(() => {
        if (personIKRs.length === 0) return null;
        
        let totalProgress = 0;
        let onTrack = 0, needsAttention = 0, offTrack = 0, exceeded = 0;
        
        personIKRs.forEach(ikr => {
            // Use the same calculation method as OKR tracking page
            // Use the original quarterlyActuals array directly (preserved from OKR tracking page)
            const ikrForProgress = {
                quarterlyActuals: ikr.quarterlyActuals || [ikr.q1Actual, ikr.q2Actual, ikr.q3Actual, ikr.q4Actual],
                kpiTarget: ikr.targetKpi || ikr.kpiTarget,
                calculationMethod: ikr.calculationMethod || 'sum'
            };
            // Prefer the figure derived from the raw OKR doc so this matches OKR Tracking.
            const progressDecimal = ikr.trackingProgress ?? calculateIkrProgress(ikrForProgress);
            const progress = progressDecimal * 100; // Convert to percentage
            totalProgress += progress;
            
            if (progress >= 100) exceeded++;
            else if (progress >= 70) onTrack++;
            else if (progress >= 30) needsAttention++;
            else offTrack++;
        });
        
        const averageProgress = personIKRs.length > 0 ? totalProgress / personIKRs.length : 0;
        
        return {
            averageProgress,
            breakdown: [
                { label: 'Exceeded', count: exceeded, color: 'bg-green-500' },
                { label: 'On Track', count: onTrack, color: 'bg-blue-500' },
                { label: 'Needs Attention', count: needsAttention, color: 'bg-yellow-500' },
                { label: 'Off Track', count: offTrack, color: 'bg-red-500' }
            ]
        };
    }, [personIKRs]);
    
    // Load evaluations from Firestore using user-specific folder structure
    useEffect(() => {
        if (!selectedPerson || !selectedPeriod) return;
        
        setLoading(true);
        const evaluationsRef = collection(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/ikrs`);
        
        const unsubscribe = onSnapshot(evaluationsRef, (snapshot) => {
            const evaluationsData = {};
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                evaluationsData[docSnap.id] = data;
            });
            setEvaluations(evaluationsData);
            setLoading(false);
        });
        
        return () => unsubscribe();
    }, [selectedPerson, selectedPeriod, dbInstance, evalSubcollection]);

    // Load evaluation cards from Firestore using user-specific folder structure
    useEffect(() => {
        if (!selectedPerson || !selectedPeriod) return;
        
        const cardsRef = collection(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/cards`);
        
        const unsubscribe = onSnapshot(cardsRef, (snapshot) => {
            const cardsData = {};
            let npsValue = null;
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                if (docSnap.id === 'npsScore') {
                    npsValue = data.content;
                } else {
                    cardsData[docSnap.id] = data.content;
                }
            });
            Object.entries(pendingCardEdits.current).forEach(([cardType, pending]) => {
                if (pending.person === selectedPerson && pending.period === selectedPeriod) {
                    cardsData[cardType] = pending.value;
                }
            });
            setEvaluationCards(cardsData);
            setNpsScore(npsValue);
        });
        
        return () => unsubscribe();
    }, [selectedPerson, selectedPeriod, dbInstance, evalSubcollection]);

    // Load meeting date from Firestore using user-specific folder structure
    useEffect(() => {
        if (!selectedPerson || !selectedPeriod) return;
        
        const meetingRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/meeting/date`);
        
        const unsubscribe = onSnapshot(meetingRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setMeetingDate(data.meetingDate || '');
            } else {
                setMeetingDate('');
            }
        });
        
        return () => unsubscribe();
    }, [selectedPerson, selectedPeriod, dbInstance, evalSubcollection]);

    // Load employee status from Firestore using user-specific folder structure
    useEffect(() => {
        if (!selectedPerson || !selectedPeriod) return;
        
        const statusRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/status/current`);
        
        const unsubscribe = onSnapshot(statusRef, (docSnap) => {
            if (docSnap.exists()) {
                const statusData = docSnap.data();
                setEmployeeStatus(statusData.status || 'pending');
            } else {
                setEmployeeStatus('pending');
            }
        });
        
        return () => unsubscribe();
    }, [selectedPerson, selectedPeriod, dbInstance, evalSubcollection]);


    const writeEvaluationCard = (cardType, value, person, period) => {
        const cardRef = doc(dbInstance, `users/${person}/${evalSubcollection}/${period}/cards/${cardType}`);
        return setDoc(cardRef, {
            cardType,
            userId: person,
            period,
            content: value,
            lastUpdated: new Date().toISOString()
        }, { merge: true });
    };

    /**
     * Keystroke handler for the evaluation and satisfaction textareas. Local state updates
     * synchronously so the controlled value never lags what is in the DOM — when it lags,
     * React rewrites the textarea and the caret jumps to the end. The Firestore write is
     * debounced so it no longer sits between the keystroke and the state update.
     */
    const handleUpdateEvaluationCard = (cardType, value) => {
        const person = selectedPerson;
        const period = selectedPeriod;

        pendingCardEdits.current[cardType] = { value, person, period };
        setEvaluationCards(prev => ({
            ...prev,
            [cardType]: value
        }));

        clearTimeout(cardWriteTimers.current[cardType]);
        cardWriteTimers.current[cardType] = setTimeout(() => {
            delete cardWriteTimers.current[cardType];
            writeEvaluationCard(cardType, value, person, period)
                .then(() => {
                    const pending = pendingCardEdits.current[cardType];
                    if (pending && pending.value === value) delete pendingCardEdits.current[cardType];
                })
                .catch(error => console.error('Error updating evaluation card:', error));
        }, CARD_WRITE_DEBOUNCE_MS);
    };

    /** Persist debounced edits right away — before submitting, closing the modal, or unmounting. */
    const flushEvaluationCardWrites = () => {
        const writes = Object.keys(cardWriteTimers.current).map(cardType => {
            clearTimeout(cardWriteTimers.current[cardType]);
            delete cardWriteTimers.current[cardType];
            const pending = pendingCardEdits.current[cardType];
            if (!pending) return null;
            return writeEvaluationCard(cardType, pending.value, pending.person, pending.period)
                .catch(error => console.error('Error updating evaluation card:', error));
        });
        return Promise.all(writes.filter(Boolean));
    };
    const flushCardWritesRef = useRef(flushEvaluationCardWrites);
    flushCardWritesRef.current = flushEvaluationCardWrites;
    useEffect(() => () => { flushCardWritesRef.current(); }, []);

    const handleUpdateNpsScore = async (score) => {
        setNpsScore(score);
        try {
            const npsRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/cards/npsScore`);
            await setDoc(npsRef, {
                cardType: 'npsScore',
                userId: selectedPerson,
                period: selectedPeriod,
                content: score,
                lastUpdated: new Date().toISOString()
            }, { merge: true });
        } catch (error) {
            console.error('Error updating NPS score:', error);
        }
    };

    const handleUpdateMeetingDate = async (date) => {
        try {
            const meetingRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/meeting/date`);
            await setDoc(meetingRef, {
                userId: selectedPerson,
                period: selectedPeriod,
                meetingDate: date,
                lastUpdated: new Date().toISOString()
            }, { merge: true });
            
            setMeetingDate(date);
        } catch (error) {
            console.error('Error updating meeting date:', error);
        }
    };

    // New handler functions for the restructured evaluation system with user-specific folders
    const handleSelfEvalSubmit = async (ikrId, selfEvalData) => {
        try {
            const evaluationRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/ikrs/${ikrId}`);
            const evaluationData = {
                userId: selectedPerson,
                ikrId: ikrId,
                period: selectedPeriod,
                selfEvaluation: selfEvalData,
                // serverTimestamp, not the ISO string this file uses elsewhere: this is
                // the "when did they fill it in" record shown back to admins, so it must
                // not be settable from a wrong client clock. The manager's later write to
                // this same document merges and never touches this key, so it survives.
                selfEvaluationSubmittedAt: serverTimestamp(),
                lastUpdated: new Date().toISOString()
            };
            
            await setDoc(evaluationRef, evaluationData, { merge: true });
            
            // Update local state
            setEvaluations(prev => ({
                ...prev,
                [ikrId]: {
                    ...prev[ikrId],
                    selfEvaluation: selfEvalData
                }
            }));
        } catch (error) {
            console.error('Error submitting self-evaluation:', error);
            throw error;
        }
    };

    const handleManagerFeedbackSubmit = async (ikrId, feedbackData) => {
        try {
            // Validate permissions: Only allow if in manager mode and user is the mapped manager
            if (evaluationMode === 'manager') {
                const selectedPersonDoc = users.find(u => (u.uid || u.id) === selectedPerson);
                if (selectedPersonDoc) {
                    const viewerId = viewer.uid || viewer.id;
                    const viewerEmail = viewer.email?.toLowerCase().trim();
                    const managerId = selectedPersonDoc.managerId;
                    const managerEmail = selectedPersonDoc.managerEmail?.toLowerCase().trim();
                    const isMappedManager = managerId === viewerId || managerEmail === viewerEmail;
                    
                    if (!isMappedManager) {
                        alert('You are not authorized to provide manager feedback for this employee.');
                        throw new Error('Unauthorized: Not the mapped manager');
                    }
                }
            } else if (evaluationMode === 'admin') {
                // Admin mode is view-only, should not reach here but add safety check
                alert('Admin mode is view-only. You cannot submit manager feedback.');
                throw new Error('Unauthorized: Admin mode is view-only');
            }
            
            const evaluationRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/ikrs/${ikrId}`);
            const evaluationData = {
                userId: selectedPerson,
                ikrId: ikrId,
                period: selectedPeriod,
                managerFeedback: feedbackData,
                lastUpdated: new Date().toISOString()
            };
            
            await setDoc(evaluationRef, evaluationData, { merge: true });
            
            // Update local state
            setEvaluations(prev => ({
                ...prev,
                [ikrId]: {
                    ...prev[ikrId],
                    managerFeedback: feedbackData
                }
            }));
        } catch (error) {
            console.error('Error submitting manager feedback:', error);
            throw error;
        }
    };

    const handleSubmitSelfEvaluations = async () => {
        try {
            // Check if NPS score is filled
            if (npsScore === null || npsScore === undefined || npsScore === '') {
                alert('Please answer the NPS question (how likely are you to recommend Rusk as a workplace) before submitting your self-evaluation.');
                return;
            }
            // Check if all 3 additional satisfaction questions are filled
            const employeeExperience = (evaluationCards['employeeExperience'] || '').trim();
            const supportSuggestions = (evaluationCards['supportSuggestions'] || '').trim();
            const otherComments = (evaluationCards['otherComments'] || '').trim();
            if (!employeeExperience) {
                alert('Please answer: "What is one thing we could change to make your employee experience better?"');
                return;
            }
            if (!supportSuggestions) {
                alert('Please answer: "Any suggestion for support teams like HR, Finance & IT?"');
                return;
            }
            if (!otherComments) {
                alert('Please answer: "Any other comments." (You may enter "N/A" if none.)');
                return;
            }
            
            // An empty list must never pass. `[].every()` is true, so without this
            // guard someone whose IKRs have not loaded yet — or who is on a financial
            // year they own nothing in — advances to manager-review with no
            // self-evaluation recorded, which locks every row they are later given.
            if (personIKRs.length === 0) {
                alert('No Key Results are loaded for this financial year and quarter yet, so there is nothing to submit. Reload the page, and check the FY and quarter are right.');
                return;
            }

            // Check if all IKRs have self-evaluations
            const allSelfEvalsComplete = personIKRs.every(ikr => {
                const evaluation = evaluations[ikr.id];
                return evaluation?.selfEvaluation?.wentWell && 
                       evaluation?.selfEvaluation?.couldBeBetter && 
                       evaluation?.selfEvaluation?.nextFocus;
            });
            
            if (!allSelfEvalsComplete) {
                alert('Please complete self-evaluations for all Key Results before submitting.');
                return;
            }
            
            // Land any debounced textarea edits before the status moves on.
            await flushEvaluationCardWrites();

            const statusRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/status/current`);
            await setDoc(statusRef, {
                userId: selectedPerson,
                period: selectedPeriod,
                status: 'manager-review',
                // The moment the employee handed the evaluation over. The next-steps and
                // complete transitions merge without this key, so it is not overwritten
                // as the evaluation moves on — unlike `lastUpdated`, which is.
                selfEvaluationSubmittedAt: serverTimestamp(),
                lastUpdated: new Date().toISOString()
            }, { merge: true });
            
            setEmployeeStatus('manager-review');
        } catch (error) {
            console.error('Error submitting self evaluations:', error);
        }
    };

    const handleSubmitManagerFeedback = async () => {
        try {
            // Same empty-list trap as the self-evaluation submit above.
            if (personIKRs.length === 0) {
                alert('No Key Results are loaded for this person in this financial year and quarter, so there is nothing to submit.');
                return;
            }

            const allManagerFeedbackComplete = personIKRs.every(ikr => {
                const evaluation = evaluations[ikr.id];
                return evaluation?.managerFeedback?.feedback && 
                       evaluation?.managerFeedback?.rating && 
                       evaluation?.managerFeedback?.managerName;
            });
            
            if (!allManagerFeedbackComplete) {
                alert('Please complete manager feedback for all Key Results before submitting.');
                return;
            }
            
            const statusRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/status/current`);
            await setDoc(statusRef, {
                userId: selectedPerson,
                period: selectedPeriod,
                status: 'next-steps',
                lastUpdated: new Date().toISOString()
            }, { merge: true });
            
            setEmployeeStatus('next-steps');
        } catch (error) {
            console.error('Error submitting manager feedback:', error);
        }
    };

    const handleSubmitNextSteps = async () => {
        try {
            const statusRef = doc(dbInstance, `users/${selectedPerson}/${evalSubcollection}/${selectedPeriod}/status/current`);
            await setDoc(statusRef, {
                userId: selectedPerson,
                period: selectedPeriod,
                status: 'complete',
                lastUpdated: new Date().toISOString()
            }, { merge: true });
            
            setEmployeeStatus('complete');
        } catch (error) {
            console.error('Error completing evaluation:', error);
        }
    };
    
    
    // Set default evaluation mode based on user capabilities (only on mount)
    useEffect(() => {
        if (!viewer) return;
        
        // Set default mode only if not already set (prevents overriding user's choice)
        // Default is employee for everyone
        if (!evaluationMode || evaluationMode === 'employee') {
            // This is already the default, no need to change
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount - evaluationMode is intentionally not in deps
    
    // Set default person based on evaluation mode
    useEffect(() => {
        if (evaluationMode === 'employee' && availablePeople.length > 0 && !selectedPerson) {
            const viewerId = viewer.uid || viewer.id;
            setSelectedPerson(viewerId);
            setPersonSearchTerm(viewer.name);
        } else if (evaluationMode === 'manager' && availablePeople.length > 0) {
            // Don't auto-select in manager mode, let user choose
        } else if (evaluationMode === 'admin' && availablePeople.length > 0) {
            // Don't auto-select in admin mode, let user choose
        }
    }, [viewer, availablePeople, selectedPerson, evaluationMode]);
    
    // Reset selected person when mode changes
    useEffect(() => {
        if (evaluationMode === 'employee' && viewer) {
            const viewerId = viewer.uid || viewer.id;
            setSelectedPerson(viewerId);
            setPersonSearchTerm(viewer.name || '');
        } else {
            // Clear selection when switching to manager or admin mode
            setSelectedPerson('');
            setPersonSearchTerm('');
        }
    }, [evaluationMode, viewer]);
    
    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowPersonDropdown(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    // Clear selection function
    const clearSelection = () => {
        setSelectedPerson('');
        setPersonSearchTerm('');
        setShowPersonDropdown(false);
    };
    
    // Early return if essential data is not ready
    if (!viewer) {
        return (
            <div className="text-center py-20 bg-white rounded-lg shadow-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading user data...</p>
            </div>
        );
    }
    
    return (
        <div className="space-y-5 max-w-7xl mx-auto">
            {/* Compact header: title, mode, person, period in one card */}
            <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                                <Award className="text-white" size={22} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Performance Evaluations</h2>
                                <p className="text-gray-500 text-xs mt-0.5">Review feedback and track progress</p>
                            </div>
                        </div>
                        {viewer.role === 'Admin' && (
                            <div className="flex flex-col items-end gap-1 shrink-0">
                                <button
                                    type="button"
                                    disabled={exportProgress !== null}
                                    onClick={async () => {
                                        // Guarded so a second click cannot start a parallel export,
                                        // and so a failure surfaces instead of vanishing into an
                                        // unhandled rejection with no file and no message.
                                        setExportError('');
                                        setExportProgress({ done: 0, total: 0 });
                                        try {
                                            const { collection, getDocs } = await import('firebase/firestore');
                                            await exportEvaluationsToExcel(
                                                users, okrs, dbProp || db, collection, getDocs,
                                                evalFY, okrsFY2627,
                                                (done, total) => setExportProgress({ done, total })
                                            );
                                        } catch (error) {
                                            console.error('Evaluations export failed:', error);
                                            setExportError('The export could not be completed. Please try again.');
                                        } finally {
                                            setExportProgress(null);
                                        }
                                    }}
                                    className="flex items-center justify-center gap-2 bg-emerald-600 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <Download size={16} />
                                    {exportProgress
                                        ? `Exporting… ${exportProgress.total ? Math.round((exportProgress.done / exportProgress.total) * 100) : 0}%`
                                        : `Export ${evalFY === 'FY2627' ? 'FY 26-27' : 'FY 25-26'} to Excel`}
                                </button>
                                {exportError && (
                                    <span className="text-[11px] text-red-600">{exportError}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        <div className="flex flex-col gap-1.5 shrink-0">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</span>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => setEvaluationMode('employee')}
                                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                        evaluationMode === 'employee' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    <User size={14} />
                                    Employee
                                </button>
                                {hasDirectReports && (
                                    <button
                                        onClick={() => setEvaluationMode('manager')}
                                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                            evaluationMode === 'manager' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        <ClipboardList size={14} />
                                        Manager
                                    </button>
                                )}
                                {viewer.role === 'Admin' && (
                                    <button
                                        onClick={() => setEvaluationMode('admin')}
                                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                            evaluationMode === 'admin' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        <ShieldCheck size={14} />
                                        Admin
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-gray-500">
                                {evaluationMode === 'employee' && 'Complete self-evaluations and satisfaction questions'}
                                {evaluationMode === 'manager' && 'Give feedback and ratings for your direct reports'}
                                {evaluationMode === 'admin' && 'View all evaluations; no edits allowed'}
                            </p>
                        </div>
                        <div className="flex flex-col gap-1.5 flex-1 min-w-0 lg:pl-4 lg:border-l border-gray-200 lg:pr-4">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Person & period</span>
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                <div className="relative flex-1 min-w-0 max-w-md" ref={dropdownRef}>
                                    <input
                                        type="text"
                                        value={personSearchTerm}
                                        onChange={(e) => { setPersonSearchTerm(e.target.value); setShowPersonDropdown(true); }}
                                        onFocus={() => setShowPersonDropdown(true)}
                                        placeholder="Search name, role, email..."
                                        className="w-full px-3 py-2 pr-8 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                                    />
                                    {selectedPerson && (
                                        <button onClick={clearSelection} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5" title="Clear">
                                            <X size={16} />
                                        </button>
                                    )}
                                    {showPersonDropdown && (
                                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-y-auto py-1">
                                            {availablePeople.length > 0 ? (
                                                availablePeople.map(person => {
                                                    const personId = person.uid || person.id;
                                                    const isSelected = selectedPerson === personId;
                                                    const uniqueKey = personId || person.email || `person-${person.name}`;
                                                    return (
                                                        <div key={uniqueKey} onClick={() => { setSelectedPerson(personId); setPersonSearchTerm(person.name); setShowPersonDropdown(false); }}
                                                            className={`px-3 py-2 cursor-pointer text-sm ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-900'}`}>
                                                            <span className="font-medium">{person.name}</span>
                                                            {person.role && <span className="text-gray-500 ml-1">· {person.role}</span>}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="px-3 py-2 text-gray-500 text-sm">No matches</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="w-full sm:w-44 shrink-0">
                                    <select
                                        value={selectedPeriod}
                                        onChange={(e) => setSelectedPeriod(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                                    >
                                        <option value="Q1">Q1 (Apr–Jun)</option>
                                        <option value="Q2">Q2 (Jul–Sep)</option>
                                        <option value="Q3">Q3 (Oct–Dec)</option>
                                        <option value="Q4">Q4 (Jan–Mar)</option>
                                        <option value="Annual">Full year</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Summary card: title, progress stepper, then KR metrics in two columns */}
            {selectedPerson && summaryMetrics && (
                <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Performance summary — {selectedPeriod}</h3>
                    <div className="mb-5">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Evaluation progress</p>
                        <div className="flex flex-wrap items-center gap-2">
                            {[
                                { stage: 'pending', label: 'Self-Review' },
                                { stage: 'manager-review', label: 'Manager Review' },
                                { stage: 'next-steps', label: 'Next Steps' },
                                { stage: 'complete', label: 'Complete' }
                            ].map((stageInfo, index) => {
                                const isCompleted = (() => {
                                    switch (employeeStatus) {
                                        case 'manager-review': return stageInfo.stage === 'pending';
                                        case 'next-steps': return stageInfo.stage === 'pending' || stageInfo.stage === 'manager-review';
                                        case 'complete': return stageInfo.stage !== 'complete';
                                        default: return false;
                                    }
                                })();
                                const isCurrent = employeeStatus === stageInfo.stage;
                                return (
                                    <span key={stageInfo.stage} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                                        isCurrent ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-200' : isCompleted ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {isCompleted && !isCurrent && <CheckCircle size={12} />}
                                        {stageInfo.label}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="flex items-center gap-4 p-4 bg-gray-50/80 rounded-xl border border-gray-100">
                            <ProgressDonut progress={summaryMetrics.averageProgress / 100} size={72} />
                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Overall KR achievement</p>
                                <p className="text-2xl font-bold text-blue-600 mt-0.5">{summaryMetrics.averageProgress.toFixed(1)}%</p>
                            </div>
                        </div>
                        <div className="p-4 bg-gray-50/80 rounded-xl border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">KR status breakdown</p>
                            <ProgressChart data={summaryMetrics.breakdown} />
                        </div>
                    </div>
                </div>
            )}
            
            {/* Employee Satisfaction – open in modal */}
            {selectedPerson && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                <ClipboardList className="text-amber-700" size={20} />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Employee satisfaction questions</h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    Answer all four before submitting your self-evaluation
                                    {(() => {
                                        const hasNps = npsScore !== null && npsScore !== undefined;
                                        const q2 = (evaluationCards['employeeExperience'] || '').trim();
                                        const q3 = (evaluationCards['supportSuggestions'] || '').trim();
                                        const q4 = (evaluationCards['otherComments'] || '').trim();
                                        const answered = (hasNps ? 1 : 0) + (q2 ? 1 : 0) + (q3 ? 1 : 0) + (q4 ? 1 : 0);
                                        if (answered === 0) return null;
                                        return ` · ${answered}/4 answered`;
                                    })()}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowSatisfactionModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors shrink-0
                                bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            <ClipboardList size={18} />
                            {evaluationMode === 'employee' && employeeStatus === 'pending' && viewer.uid === selectedPerson
                                ? (npsScore !== null && npsScore !== undefined && (evaluationCards['employeeExperience'] || '').trim() && (evaluationCards['supportSuggestions'] || '').trim() && (evaluationCards['otherComments'] || '').trim())
                                    ? 'View or edit satisfaction form'
                                    : 'Open satisfaction form'
                                : 'View satisfaction responses'}
                        </button>
                    </div>
                </div>
            )}

            <EmployeeSatisfactionModal
                isOpen={showSatisfactionModal}
                onClose={() => { flushEvaluationCardWrites(); setShowSatisfactionModal(false); }}
                npsScore={npsScore}
                onNpsChange={handleUpdateNpsScore}
                evaluationCards={evaluationCards}
                onCardChange={handleUpdateEvaluationCard}
                canEdit={evaluationMode === 'employee' && employeeStatus === 'pending' && viewer.uid === selectedPerson}
            />
            
            {/* Detailed Evaluation Table */}
            {selectedPerson && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 sm:px-8 py-5 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Detailed evaluation</h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    Review and provide feedback for each Key Result
                                </p>
                            </div>
                            {(() => {
                                const statusInfo = {
                                    'pending': { label: 'Pending Self-Review', color: 'bg-amber-100 text-amber-800 border-amber-200' },
                                    'manager-review': { label: 'Manager Review', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                                    'next-steps': { label: 'Next Steps', color: 'bg-violet-100 text-violet-800 border-violet-200' },
                                    'complete': { label: 'Complete', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' }
                                };
                                const currentStatus = statusInfo[employeeStatus] || statusInfo['pending'];
                                return (
                                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${currentStatus.color}`}>
                                        {currentStatus.label}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>
                    
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto" />
                            <p className="text-gray-500 mt-3 text-sm">Loading evaluations…</p>
                        </div>
                    ) : personIKRs.length === 0 ? (
                        /* Say why the table is empty instead of hiding the whole section. */
                        <div className="p-12 text-center">
                            <Target size={40} className="mx-auto text-gray-300" />
                            <p className="text-gray-700 font-medium mt-3">
                                No {evalFY === 'FY2627' ? 'FY 26-27' : 'FY 25-26'} individual key results are assigned to{' '}
                                {users.find(u => (u.uid || u.id) === selectedPerson)?.name || 'this person'}.
                            </p>
                            <p className="text-gray-500 text-sm mt-1">
                                Assign them an owner thread on the OKR Tracking page, or switch the financial year above.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Key Result
                                        </th>
                                        <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            {selectedPeriod} Actual
                                        </th>
                                        <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Progress
                                        </th>
                                        <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Self-Evaluation
                                        </th>
                                        <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Manager Feedback
                                        </th>
                                        <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Rating
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {personIKRs.map((ikr, idx) => (
                                        <EvaluationRow
                                            key={ikr.id}
                                            ikr={ikr}
                                            evaluation={evaluations[ikr.id]}
                                            viewer={viewer}
                                            period={selectedPeriod}
                                            employeeStatus={employeeStatus}
                                            onSelfEvalSubmit={handleSelfEvalSubmit}
                                            onManagerFeedbackSubmit={handleManagerFeedbackSubmit}
                                            managers={users.filter(u => ['Admin', 'L1 Team Lead', 'L2 Team Lead'].includes(u.role))}
                                            evaluationMode={evaluationMode}
                                            selectedPerson={selectedPerson}
                                            users={users}
                                            rowIndex={idx}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Submit area */}
                    <div className="px-6 sm:px-8 py-5 border-t border-gray-100 bg-gray-50/50">
                        {employeeStatus === 'pending' && viewer.uid === selectedPerson && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-sm text-gray-600 max-w-xl">
                                    Complete self-evaluations and all 4 satisfaction questions above, then submit to send to your manager.
                                </p>
                                <button
                                    onClick={handleSubmitSelfEvaluations}
                                    className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shrink-0"
                                >
                                    Submit Self-Evaluations
                                </button>
                            </div>
                        )}
                        {employeeStatus === 'manager-review' && (viewer.role === 'Admin' || viewer.role === 'L1 Team Lead' || viewer.role === 'L2 Team Lead') && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-sm text-gray-600 max-w-xl">
                                    Add feedback and ratings for each Key Result, then submit to move to next steps.
                                </p>
                                <button
                                    onClick={handleSubmitManagerFeedback}
                                    className="px-5 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors shrink-0"
                                >
                                    Submit Manager Feedback
                                </button>
                            </div>
                        )}
                        {employeeStatus === 'next-steps' && viewer.role === 'Admin' && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-sm text-gray-600 max-w-xl">
                                    Fill in the evaluation cards below, then submit to finalize.
                                </p>
                                <button
                                    onClick={handleSubmitNextSteps}
                                    className="px-5 py-2.5 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 transition-colors shrink-0"
                                >
                                    Complete Evaluation
                                </button>
                            </div>
                        )}
                        {employeeStatus === 'complete' && (
                            <div className="flex items-center justify-center py-2">
                                <div className="flex items-center gap-2 text-emerald-600">
                                    <CheckCircle size={22} />
                                    <span className="font-medium">Evaluation complete</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Admin: Evaluation cards and meeting date */}
            {selectedPerson && viewer.role === 'Admin' && (employeeStatus === 'next-steps' || employeeStatus === 'complete') && (
                <div className="mt-8 space-y-6">
                    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Calendar size={20} className="text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Evaluation meeting</h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="text-sm font-medium text-gray-700">Scheduled date</label>
                            <input
                                type="date"
                                value={meetingDate}
                                onChange={(e) => handleUpdateMeetingDate(e.target.value)}
                                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        {meetingDate && (
                            <p className="text-sm text-gray-600 mt-3">
                                Meeting scheduled for {new Date(meetingDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                    <Target size={18} className="text-emerald-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Strengths</h3>
                            </div>
                            <textarea
                                value={evaluationCards.strengths || ''}
                                onChange={(e) => handleUpdateEvaluationCard('strengths', e.target.value)}
                                placeholder="Areas where the employee excelled or exceeded expectations..."
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y min-h-[120px]"
                                rows={6}
                            />
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <TrendingUp size={18} className="text-amber-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Areas for Growth</h3>
                            </div>
                            <textarea
                                value={evaluationCards.growthAreas || ''}
                                onChange={(e) => handleUpdateEvaluationCard('growthAreas', e.target.value)}
                                placeholder="Areas to improve or develop..."
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y min-h-[120px]"
                                rows={6}
                            />
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Rocket size={18} className="text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Next Steps</h3>
                            </div>
                            <textarea
                                value={evaluationCards.nextSteps || ''}
                                onChange={(e) => handleUpdateEvaluationCard('nextSteps', e.target.value)}
                                placeholder="Next steps and development plans..."
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[120px]"
                                rows={6}
                            />
                        </div>
                    </div>

                    {/* Permission Information */}
                    <div className="bg-blue-50/80 border border-blue-100 p-5 rounded-xl">
                        <div className="flex items-start gap-3">
                            <ShieldCheck size={20} className="text-blue-600 mt-0.5 shrink-0" />
                            <div>
                                <h4 className="text-sm font-medium text-blue-900">Evaluation Permissions</h4>
                                <div className="text-sm text-blue-800 mt-1 space-y-1">
                                    <p><strong>4-Stage Evaluation Flow:</strong></p>
                                    <ul className="ml-4 space-y-1">
                                        <li>• <strong>Pending Self-Review:</strong> Employee writes self-evaluations</li>
                                        <li>• <strong>Pending Manager Review:</strong> Manager provides feedback and ratings</li>
                                        <li>• <strong>Pending Next Steps:</strong> Admin completes evaluation cards</li>
                                        <li>• <strong>Complete:</strong> Evaluation finalized</li>
                                    </ul>
                                    <p><strong>Submit Buttons:</strong></p>
                                    <ul className="ml-4 space-y-1">
                                        <li>• <strong>Stage 1:</strong> Employee submits self-evaluations</li>
                                        <li>• <strong>Stage 2:</strong> Manager submits feedback/ratings</li>
                                        <li>• <strong>Stage 3:</strong> Admin submits evaluation cards</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Evaluation cards – read-only for non-admins */}
            {selectedPerson && viewer.role !== 'Admin' && (evaluationCards.strengths || evaluationCards.growthAreas || evaluationCards.nextSteps || meetingDate) && (
                <div className="mt-8 space-y-6">
                    {meetingDate && (
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Calendar size={20} className="text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Evaluation meeting</h3>
                            </div>
                            <p className="text-sm text-gray-600">
                                Scheduled for {new Date(meetingDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.
                            </p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {evaluationCards.strengths && (
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                        <Target size={18} className="text-emerald-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Strengths</h3>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{evaluationCards.strengths}</p>
                            </div>
                        )}
                        {evaluationCards.growthAreas && (
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                        <TrendingUp size={18} className="text-amber-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Areas for Growth</h3>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{evaluationCards.growthAreas}</p>
                            </div>
                        )}
                        {evaluationCards.nextSteps && (
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                        <Rocket size={18} className="text-blue-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Next Steps</h3>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{evaluationCards.nextSteps}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Empty: no Key Results */}
            {selectedPerson && personIKRs.length === 0 && (
                <div className="text-center py-16 px-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">
                        <Target size={32} className="text-gray-400" />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-gray-800">No Key Results for this period</h3>
                    <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                        {selectedPerson === viewer.uid
                            ? "You don't have any assigned Key Results for this period."
                            : "This person has no assigned Key Results for this period."
                        }
                    </p>
                </div>
            )}
            
            {/* No person selected */}
            {!selectedPerson && (
                <div className="text-center py-16 px-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">
                        <User size={32} className="text-gray-400" />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-gray-800">Select a person</h3>
                    <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                        Use the search above to choose an employee and view their evaluations for the selected period.
                    </p>
                </div>
            )}
        </div>
    );
}
